import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Spinner from '../../Shared/Spinner';
import styles from './Finances.module.css';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, ChartTooltip, Legend, Filler);

interface Transaction {
  id: number;
  mercenaryId: number;
  amount: number;
  source: string;
  description: string | null;
  balanceAfter: number | null;
  metadata: any;
  createdAt: string;
}

interface SourceBreakdown {
  source: string;
  gained: number;
  spent: number;
  net: number;
  count: number;
}

interface DailyTotal {
  date: string;
  gained: number;
  spent: number;
  net: number;
}

interface FinanceSummary {
  currentSilver: number;
  totals: {
    gained: number;
    spent: number;
    net: number;
    totalTransactions: number;
  };
  sourceBreakdown: SourceBreakdown[];
  dailyTotals: DailyTotal[];
}

interface Pagination {
  page: number;
  limit: number;
  totalPages: number;
  totalItems: number;
}

// Human-readable source labels
const SOURCE_LABELS: Record<string, string> = {
  beast_battle: 'Beast Battle',
  duel: 'Duel',
  contract_battle: 'Contract Battle',
  tournament: 'Tournament Combat',
  tournament_reward: 'Tournament Reward',
  tournament_bet: 'Tournament Bet',
  tournament_bet_payout: 'Bet Payout',
  tournament_bet_refund: 'Bet Refund',
  team_battle: 'Team Battle',
  guild_league: 'Guild League',
  work_district: 'Work District',
  tavern_food: 'Tavern Food',
  gambling_win: 'Gambling Win',
  gambling_loss: 'Gambling Loss',
  vendor_purchase: 'Vendor Purchase',
  auction_fee: 'Auction Fee',
  auction_sale: 'Auction Sale',
  auction_purchase: 'Auction Purchase',
  auction_refund: 'Auction Refund',
  vault_deposit: 'Vault Deposit',
  vault_withdraw: 'Vault Withdrawal',
  guild_vault_deposit: 'Guild Vault Deposit',
  guild_vault_withdraw: 'Guild Vault Withdrawal',
  city_building_contribution: 'City Contribution',
  guild_building_contribution: 'Guild Contribution',
  orange_merchant_purchase: 'Orange Merchant',
  admin: 'Admin Adjustment',
  starting_silver: 'Starting Silver',
  daily_missions_reward: 'Daily Mission Reward',
};

// Colors for the doughnut chart slices
const SOURCE_COLORS = [
  '#d4af37', '#48bb78', '#5b9bd5', '#ed8936', '#9f7aea',
  '#f56565', '#38b2ac', '#e53e8e', '#dd6b20', '#667eea',
  '#4fd1c5', '#fc8181', '#b794f4', '#f6ad55', '#68d391',
  '#63b3ed', '#feb2b2', '#c6f6d5', '#fefcbf', '#e9d8fd',
];

function formatSilver(amount: number): string {
  return amount.toLocaleString();
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} - ${hh}:${min}:${ss}`;
}

const Finances: React.FC = () => {
  const { mercenaryId } = useMercenary();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, totalPages: 1, totalItems: 0 });
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<'source' | 'gained' | 'spent' | 'net' | 'count'>('net');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchSummary = useCallback(async () => {
    if (!mercenaryId) return;
    try {
      const res = await axios.get(`/api/finance/${mercenaryId}/summary`, { withCredentials: true });
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch finance summary:', err);
    }
  }, [mercenaryId]);

  const fetchTransactions = useCallback(async (p: number = 1, source: string = 'all') => {
    if (!mercenaryId) return;
    try {
      const params: any = { page: p, limit: 50 };
      if (source !== 'all') params.source = source;
      const res = await axios.get(`/api/finance/${mercenaryId}/transactions`, { withCredentials: true, params });
      setTransactions(res.data.transactions);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  }, [mercenaryId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchTransactions(1, 'all')]);
      setLoading(false);
    };
    loadData();
  }, [mercenaryId, fetchSummary, fetchTransactions]);

  const handleSourceFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSourceFilter(val);
    setPage(1);
    fetchTransactions(1, val);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchTransactions(newPage, sourceFilter);
  };

  // Read theme colors from CSS variables at render time
  const getThemeColor = (varName: string, fallback: string): string => {
    if (typeof document === 'undefined') return fallback;
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback;
  };

  if (loading) {
    return <div className={styles.loadingContainer}><Spinner /></div>;
  }

  if (!summary || summary.totals.totalTransactions === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>&#x1F4B0;</div>
        <div className={styles.emptyText}>No financial records yet. Start earning silver to see your finances here!</div>
      </div>
    );
  }

  // --- Chart Data ---

  const textColor = getThemeColor('--text-main', '#e8dcc4');
  const mutedColor = getThemeColor('--text-muted', '#787469');
  const gridColor = 'rgba(255, 255, 255, 0.06)';
  const successColor = getThemeColor('--color-status-success', '#48bb78');
  const errorColor = getThemeColor('--color-status-error', '#f56565');

  // Line chart: daily silver flow
  const lineData = {
    labels: summary.dailyTotals.map(d => formatDate(d.date)),
    datasets: [
      {
        label: 'Gained',
        data: summary.dailyTotals.map(d => d.gained),
        borderColor: successColor,
        backgroundColor: successColor + '20',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'Spent',
        data: summary.dailyTotals.map(d => d.spent),
        borderColor: errorColor,
        backgroundColor: errorColor + '20',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const lineOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        labels: { color: textColor, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()} silver`,
        },
      },
    },
    scales: {
      x: { ticks: { color: mutedColor, font: { size: 10 } }, grid: { color: gridColor } },
      y: { ticks: { color: mutedColor, font: { size: 10 }, callback: (v: number) => formatSilver(v) }, grid: { color: gridColor } },
    },
  };

  // Doughnut chart: income by source (top sources by gained amount)
  const incomeBreakdown = summary.sourceBreakdown
    .filter(s => s.gained > 0)
    .sort((a, b) => b.gained - a.gained)
    .slice(0, 10);

  const doughnutData = {
    labels: incomeBreakdown.map(s => SOURCE_LABELS[s.source] || s.source),
    datasets: [{
      data: incomeBreakdown.map(s => s.gained),
      backgroundColor: incomeBreakdown.map((_, i) => SOURCE_COLORS[i % SOURCE_COLORS.length]),
      borderColor: 'rgba(0,0,0,0.3)',
      borderWidth: 1,
    }],
  };

  const doughnutOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: { color: textColor, font: { size: 10 }, padding: 8, boxWidth: 12 },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const pct = ((ctx.raw / total) * 100).toFixed(1);
            return `${ctx.label}: ${ctx.raw.toLocaleString()} silver (${pct}%)`;
          },
        },
      },
    },
  };

  // All unique sources for the filter dropdown
  const allSources = summary.sourceBreakdown.map(s => s.source).sort();

  // Sort breakdown by selected column
  const handleSort = (key: 'source' | 'gained' | 'spent' | 'net' | 'count') => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedBreakdown = [...summary.sourceBreakdown].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'source') {
      const labelA = SOURCE_LABELS[a.source] || a.source;
      const labelB = SOURCE_LABELS[b.source] || b.source;
      return dir * labelA.localeCompare(labelB);
    }
    return dir * ((a[sortKey] as number) - (b[sortKey] as number));
  });

  return (
    <div className={styles.financesContainer}>
      {/* Summary Cards */}
      <div className={styles.summaryRow}>
        <div className={`${styles.summaryCard} ${styles.summaryCardGained}`}>
          <div className={styles.summaryLabel}>Total Earned</div>
          <div className={`${styles.summaryValue} ${styles.summaryValueGained}`}>
            +{formatSilver(summary.totals.gained)}
            {/*<span className={styles.silverIcon}>&#9733;</span>*/}
          </div>
        </div>
        <div className={`${styles.summaryCard} ${styles.summaryCardSpent}`}>
          <div className={styles.summaryLabel}>Total Spent</div>
          <div className={`${styles.summaryValue} ${styles.summaryValueSpent}`}>
            -{formatSilver(summary.totals.spent)}
            {/*<span className={styles.silverIcon}>&#9733;</span>*/}
          </div>
        </div>
        <div className={`${styles.summaryCard} ${styles.summaryCardNet}`}>
          <div className={styles.summaryLabel}>Net Balance</div>
          <div className={`${styles.summaryValue} ${summary.totals.net >= 0 ? styles.summaryValuePositive : styles.summaryValueNegative}`}>
            {summary.totals.net >= 0 ? '+' : ''}{formatSilver(summary.totals.net)}
            {/*<span className={styles.silverIcon}>&#9733;</span>*/}
          </div>
          <div className={styles.currentBalance}>
            Current: <span className={styles.currentBalanceAmount}>{summary.currentSilver.toLocaleString()}</span> silver
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className={styles.chartsRow}>
        <div className={styles.chartCard}>
          <div className={styles.chartTitle}>Silver Flow (Last 30 Days)</div>
          <div className={styles.chartWrapper}>
            {summary.dailyTotals.length > 0 ? (
              <Line data={lineData} options={lineOptions} />
            ) : (
              <div className={styles.emptyState}><div className={styles.emptyText}>Not enough data yet</div></div>
            )}
          </div>
        </div>
        <div className={styles.chartCard}>
          <div className={styles.chartTitle}>Income Sources</div>
          <div className={styles.chartWrapper}>
            {incomeBreakdown.length > 0 ? (
              <Doughnut data={doughnutData} options={doughnutOptions} />
            ) : (
              <div className={styles.emptyState}><div className={styles.emptyText}>No income recorded</div></div>
            )}
          </div>
        </div>
      </div>

      {/* Source Breakdown Table */}
      <div className={styles.breakdownCard}>
        <div className={styles.chartTitle}>Breakdown by Source</div>
        <div className={styles.breakdownTableScroller}>
          <table className={styles.breakdownTable}>
            <thead>
              <tr>
                <th className={styles.sortableHeader} onClick={() => handleSort('source')}>Source {sortKey === 'source' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className={styles.sortableHeader} onClick={() => handleSort('gained')}>Earned {sortKey === 'gained' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className={styles.sortableHeader} onClick={() => handleSort('spent')}>Spent {sortKey === 'spent' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className={styles.sortableHeader} onClick={() => handleSort('net')}>Net {sortKey === 'net' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className={styles.sortableHeader} onClick={() => handleSort('count')}>Transactions {sortKey === 'count' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
              </tr>
            </thead>
            <tbody>
              {sortedBreakdown.map((s, i) => (
                <tr key={s.source}>
                  <td>
                    <span className={styles.sourceLabel}>
                      <span className={styles.sourceDot} style={{ backgroundColor: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />
                      {SOURCE_LABELS[s.source] || s.source}
                    </span>
                  </td>
                  <td className={styles.gained}>+{s.gained.toLocaleString()}</td>
                  <td className={styles.spent}>-{s.spent.toLocaleString()}</td>
                  <td className={s.net >= 0 ? styles.positive : styles.negative}>
                    {s.net >= 0 ? '+' : ''}{s.net.toLocaleString()}
                  </td>
                  <td>{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Log */}
      <div className={styles.logCard}>
        <div className={styles.logHeader}>
          <div className={styles.chartTitle} style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Transaction Log</div>
          <div className={styles.logFilters}>
            <select className={styles.filterSelect} value={sourceFilter} onChange={handleSourceFilterChange}>
              <option value="all">All Sources</option>
              {allSources.map(s => (
                <option key={s} value={s}>{SOURCE_LABELS[s] || s}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.transactionList}>
          {transactions.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyText}>No transactions found</div>
            </div>
          ) : (
            transactions.map(tx => (
              <div key={tx.id} className={styles.transactionRow}>
                <span className={styles.txDate}>{formatDateTime(tx.createdAt)}</span>
                <span className={styles.txDescription}>
                  {tx.description || (SOURCE_LABELS[tx.source] || tx.source)}
                  <span className={styles.txSourceBadge}>{SOURCE_LABELS[tx.source] || tx.source}</span>
                </span>
                <span className={`${styles.txAmount} ${tx.amount >= 0 ? styles.positive : styles.negative}`}>
                  {tx.amount >= 0 ? '+' : ''}{tx.amount.toLocaleString()}
                </span>
              </div>
            ))
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.paginationBtn}
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              &#8249; Prev
            </button>
            <span className={styles.paginationInfo}>
              Page {page} of {pagination.totalPages}
            </span>
            <button
              className={styles.paginationBtn}
              disabled={page >= pagination.totalPages}
              onClick={() => handlePageChange(page + 1)}
            >
              Next &#8250;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Finances;
